import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import SearchableDropdown from '../components/SearchableDropdown';
import LoadingSpinner from '../components/LoadingSpinner';
import '../App.css';

interface League {
  id: string;
  name: string;
  season: string;
}

interface TopPlayer {
  player: {
    id: string;
    firstName: string;
    lastName: string;
    email?: string;
  };
  teams: Array<{ id: string; name: string; teamNumber: string }>;
  statistics: {
    goals: number;
    assists: number;
    yellowCards: number;
    redCards: number;
    matchesPlayed: number;
  };
  rank: number;
}

interface StatsData {
  league: League;
  category: string;
  players: TopPlayer[];
}

const Stats: React.FC = () => {
  const { user } = useAuth();
  const [leagues, setLeagues] = useState<Array<{ id: string; label: string; subtitle?: string }>>([]);
  const [selectedLeague, setSelectedLeague] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('goals');
  const [statsData, setStatsData] = useState<StatsData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLeagues = async () => {
      try {
        const response = await api.get('/leagues', { params: { page: '1', limit: '100' } });
        const leaguesData = response.data.leagues || response.data;
        setLeagues(leaguesData
          .filter((l: League) => l && l.id && l.name)
          .map((l: League) => ({ 
            id: l.id, 
            label: l.name || 'Unknown League', 
            subtitle: l.season || '' 
          }))
        );
        if (leaguesData.length > 0) {
          setSelectedLeague(leaguesData[0].id);
        }
      } catch (error) {
        console.error('Error fetching leagues:', error);
      }
    };

    fetchLeagues();
  }, []);

  useEffect(() => {
    if (selectedLeague) {
      fetchStats();
    }
  }, [selectedLeague, selectedCategory]);

  const fetchStats = async () => {
    if (!selectedLeague) return;
    
    setLoading(true);
    try {
      const response = await api.get('/stats/top-players', {
        params: {
          leagueId: selectedLeague,
          category: selectedCategory,
          limit: '15'
        }
      });
      setStatsData(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'goals': return 'Goals';
      case 'assists': return 'Assists';
      case 'yellowCards': return 'Yellow Cards';
      case 'redCards': return 'Red Cards';
      default: return category;
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'goals': return '⚽';
      case 'assists': return '🎯';
      case 'yellowCards': return '🟨';
      case 'redCards': return '🟥';
      default: return '';
    }
  };

  const getCategoryValue = (player: TopPlayer, category: string) => {
    return player.statistics[category as keyof typeof player.statistics] as number;
  };

  if (loading && !statsData) {
    return <LoadingSpinner fullPage />;
  }

  return (
    <div className="container">
      <h1>Player Statistics</h1>

      <div className="card" style={{ marginBottom: '20px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
          <div className="form-group">
            <label>Select League</label>
            <SearchableDropdown
              options={leagues}
              value={selectedLeague}
              onChange={setSelectedLeague}
              placeholder="Select a league..."
            />
          </div>

          <div className="form-group">
            <label>Category</label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              style={{ width: '100%' }}
            >
              <option value="goals">Goals</option>
              <option value="assists">Assists</option>
              <option value="yellowCards">Yellow Cards</option>
              <option value="redCards">Red Cards</option>
            </select>
          </div>
        </div>
      </div>

      {statsData && (
        <div className="card">
          <div style={{ marginBottom: '20px' }}>
            <h2>
              Top 15 Players - {getCategoryIcon(selectedCategory)} {getCategoryLabel(selectedCategory)}
            </h2>
            <p style={{ color: '#757575' }}>
              <strong>League:</strong> {statsData.league.name}
            </p>
          </div>

          {statsData.players.length === 0 ? (
            <p>No statistics available for this league and category.</p>
          ) : (
            <div className="table-wrapper">
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: '50px' }}>Rank</th>
                  <th>Player</th>
                  <th>Team(s)</th>
                  <th>{getCategoryLabel(selectedCategory)}</th>
                  <th>Matches</th>
                  {selectedCategory === 'goals' && <th>Assists</th>}
                  {selectedCategory === 'assists' && <th>Goals</th>}
                  {(selectedCategory === 'goals' || selectedCategory === 'assists') && (
                    <>
                      <th>Yellow Cards</th>
                      <th>Red Cards</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {statsData.players.map((player) => (
                  <tr key={player.player.id}>
                    <td>
                      <strong style={{ fontSize: '18px', color: player.rank <= 3 ? '#FFC107' : '#757575' }}>
                        #{player.rank}
                      </strong>
                    </td>
                    <td>
                      <Link 
                        to={`/players/${player.player.id}`}
                        style={{ color: '#2E7D32', textDecoration: 'none', fontWeight: '500' }}
                      >
                        {player.player.firstName} {player.player.lastName}
                      </Link>
                    </td>
                    <td>
                      {player.teams.map((team, idx) => (
                        <span key={team.id}>
                          <Link 
                            to={`/teams/${team.id}`}
                            style={{ color: '#2E7D32', textDecoration: 'none' }}
                          >
                            {team.name}
                          </Link>
                          {idx < player.teams.length - 1 && ', '}
                        </span>
                      ))}
                    </td>
                    <td>
                      <strong style={{ fontSize: '18px', color: '#2E7D32' }}>
                        {getCategoryValue(player, selectedCategory)}
                      </strong>
                    </td>
                    <td>{player.statistics.matchesPlayed}</td>
                    {selectedCategory === 'goals' && (
                      <td>{player.statistics.assists}</td>
                    )}
                    {selectedCategory === 'assists' && (
                      <td>{player.statistics.goals}</td>
                    )}
                    {(selectedCategory === 'goals' || selectedCategory === 'assists') && (
                      <>
                        <td>
                          {player.statistics.yellowCards > 0 && (
                            <span className="badge badge-warning">
                              🟨 {player.statistics.yellowCards}
                            </span>
                          )}
                          {player.statistics.yellowCards === 0 && '-'}
                        </td>
                        <td>
                          {player.statistics.redCards > 0 && (
                            <span className="badge badge-danger">
                              🟥 {player.statistics.redCards}
                            </span>
                          )}
                          {player.statistics.redCards === 0 && '-'}
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Stats;

